import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Container, Paper, FormControl, FormControlLabel, 
  RadioGroup, Radio, Button, CircularProgress, Alert, Snackbar,
  Grid, LinearProgress, Chip, Select, MenuItem, InputLabel,
  Link, Checkbox, ListItemText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FeedbackIcon from '@mui/icons-material/Feedback';

import { fetchReceipts, fetchReceiptItemsWithTags, fetchItemCategories } from '../utils/receiptsUtils';
import { useTags } from '../hooks/useTags';

// Import new components
import StepIndicator from './exportcenter/StepIndicator';
import UseCasesSection from './exportcenter/UseCasesSection';
import ReceiptSelector from './exportcenter/ReceiptSelector';
import ExportOptionsPanel from './exportcenter/ExportOptionsPanel';
import { generatePDFWithImages } from './exportcenter/pdfGenerator';

// Background decoration component
const BackgroundOrbs = () => (
  <>
    <Box sx={{ position: 'absolute', top: 40, left: '10%', width: 400, height: 400, 
      background: 'rgba(59, 130, 246, 0.1)', borderRadius: '50%', filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 }} />
    <Box sx={{ position: 'absolute', top: 200, right: '10%', width: 350, height: 350, 
      background: 'rgba(139, 92, 246, 0.1)', borderRadius: '50%', filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 }} />
  </>
);

function ExportCenter() {
  const navigate = useNavigate();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Export mode
  const [exportMode, setExportMode] = useState('bulk'); // 'bulk' or 'individual'
  
  // Step 2: Configuration (bulk mode)
  const [exportType, setExportType] = useState('item');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  
  // Step 2: Configuration (individual mode)
  const [selectedReceipts, setSelectedReceipts] = useState([]);
  
  // Step 3: Export options
  const [exportFormat, setExportFormat] = useState('csv');
  const [imageLayout, setImageLayout] = useState('one_per_page');
  const [imageQuality, setImageQuality] = useState('optimized');
  const [includeLineItems, setIncludeLineItems] = useState(true);
  const [includePageNumbers, setIncludePageNumbers] = useState(true);
  const [includeImageAppendix, setIncludeImageAppendix] = useState(false);
  const [sortOption, setSortOption] = useState('none');
  
  // Data
  const [receipts, setReceipts] = useState([]);
  const [filteredReceipts, setFilteredReceipts] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const { tags: availableTags, isLoading: tagsLoading } = useTags();
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingReceipts, setIsLoadingReceipts] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });

  // Auth check & data loading
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth?mode=login');
    } else {
      loadData(token);
    }
  }, [navigate]);

  const loadData = async (token) => {
    setIsLoadingReceipts(true);
    try {
      const [receiptsData, categoriesData] = await Promise.all([
        fetchReceipts(token),
        fetchItemCategories()
      ]);
      setReceipts(receiptsData || []);
      setFilteredReceipts(receiptsData || []);
      if (categoriesData?.categories) {
        setAvailableCategories(categoriesData.categories.map(cat => ({ name: cat.name, emoji: cat.emoji })));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Failed to load receipts', 'error');
    } finally {
      setIsLoadingReceipts(false);
    }
  };

  // Filter receipts by date
  useEffect(() => {
    if (receipts.length === 0) return;
    let filtered = receipts;
    if (startDate || endDate) {
      filtered = receipts.filter(receipt => {
        const receiptDate = new Date(getEffectiveValue(receipt, 'date'));
        if (isNaN(receiptDate.getTime())) return false;
        if (startDate && endDate) return receiptDate >= startDate && receiptDate <= endDate;
        if (startDate) return receiptDate >= startDate;
        if (endDate) return receiptDate <= endDate;
        return true;
      });
    }
    setFilteredReceipts(filtered);
  }, [startDate, endDate, receipts]);

  const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast({ ...toast, open: false });
  
  const getEffectiveValue = (obj, field) => {
    const editedField = `${field}_edited`;
    return obj[editedField] && obj[editedField] !== '' ? obj[editedField] : obj[field];
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Get receipts for export based on mode
  const receiptsToExport = useMemo(() => {
    if (exportMode === 'individual') {
      return receipts.filter(r => selectedReceipts.includes(r.uuid));
    }
    return filteredReceipts;
  }, [exportMode, selectedReceipts, filteredReceipts, receipts]);

  // Navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 1: return true;
      case 2: return exportMode === 'individual' ? selectedReceipts.length > 0 : filteredReceipts.length > 0;
      case 3: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < 4 && canGoNext()) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleStepClick = (step) => {
    if (step < currentStep) setCurrentStep(step);
  };

  // Export functions
  const handleExport = async () => {
    if (exportFormat === 'csv') {
      await generateCSV();
    } else if (exportFormat === 'pdf') {
      await generatePDFReport();
    } else if (exportFormat === 'pdf_images') {
      await generatePDFWithImagesExport();
    }
  };

  const generateCSV = async () => {
    setIsLoading(true);
    setProgress({ current: 0, total: 0, message: 'Preparing export...' });
    try {
      const token = localStorage.getItem('token');
      const receiptsForExport = receiptsToExport;
      
      // Determine if we should export at receipt level or line item level
      const shouldExportReceiptLevel = exportType === 'receipt';
      
      if (shouldExportReceiptLevel) {
        // Receipt-level export
        const csvData = [['Receipt ID', 'Merchant', 'Date', 'Total Cost', 'Tax', 'Tip']];
        receiptsForExport.forEach(receipt => {
          csvData.push([
            receipt.uuid,
            getEffectiveValue(receipt, 'merchant_name'),
            formatDate(getEffectiveValue(receipt, 'date')),
            getEffectiveValue(receipt, 'total_cost'),
            getEffectiveValue(receipt, 'maybe_receipt_tax'),
            getEffectiveValue(receipt, 'maybe_receipt_tip')
          ]);
        });
        downloadCSV(csvData, 'receipts_export');
      } else {
        // Line item-level export
        const csvData = [['Receipt ID', 'Merchant', 'Date', 'Item Name', 'Price', 'Category', 'Tags']];
        for (let i = 0; i < receiptsForExport.length; i++) {
          const receipt = receiptsForExport[i];
          setProgress({ current: i + 1, total: receiptsForExport.length, message: `Processing ${i + 1}/${receiptsForExport.length}...` });
          const items = await fetchReceiptItemsWithTags(token, receipt.uuid);
          let filteredItems = items;
          
          // Only apply tag/category filters in bulk mode
          const hasFilters = exportMode === 'bulk' && (selectedTags.length > 0 || selectedCategories.length > 0);
          if (exportMode === 'bulk') {
            if (selectedTags.length > 0) {
              filteredItems = filteredItems.filter(item => item.tags?.some(tag => selectedTags.includes(tag.tag_id)));
            }
            if (selectedCategories.length > 0) {
              filteredItems = filteredItems.filter(item => selectedCategories.includes(getEffectiveValue(item, 'high_level_category')));
            }
          }
          
          filteredItems.forEach(item => {
            csvData.push([
              receipt.uuid,
              getEffectiveValue(receipt, 'merchant_name'),
              formatDate(getEffectiveValue(receipt, 'date')),
              getEffectiveValue(item, 'item_name'),
              getEffectiveValue(item, 'item_price'),
              getEffectiveValue(item, 'high_level_category'),
              item.tags?.map(t => t.name).join(', ') || ''
            ]);
          });
          
          // Add tax and tip as pseudo line items (skip if filters are applied in bulk mode)
          if (!hasFilters) {
            const tax = getEffectiveValue(receipt, 'maybe_receipt_tax');
            if (tax && tax !== '0' && tax !== '') {
              csvData.push([
                receipt.uuid,
                getEffectiveValue(receipt, 'merchant_name'),
                formatDate(getEffectiveValue(receipt, 'date')),
                'Tax',
                tax,
                'Tax',
                ''
              ]);
            }
            
            const tip = getEffectiveValue(receipt, 'maybe_receipt_tip');
            if (tip && tip !== '0' && tip !== '') {
              csvData.push([
                receipt.uuid,
                getEffectiveValue(receipt, 'merchant_name'),
                formatDate(getEffectiveValue(receipt, 'date')),
                'Tip',
                tip,
                'Tip',
                ''
              ]);
            }
          }
        }
        downloadCSV(csvData, 'receipt_items_export');
      }
      showToast('Export completed!');
    } catch (error) {
      console.error('Export error:', error);
      showToast('Export failed', 'error');
    } finally {
      setIsLoading(false);
      setProgress({ current: 0, total: 0, message: '' });
    }
  };

  const generatePDFReport = async () => {
    setIsLoading(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const receiptsForExport = receiptsToExport;
      
      doc.setFontSize(20);
      doc.text('Receipt Export Report', 20, 20);
      doc.setFontSize(12);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 30);
      doc.text(`Total Receipts: ${receiptsForExport.length}`, 20, 40);
      
      const total = receiptsForExport.reduce((sum, r) => sum + (parseFloat(getEffectiveValue(r, 'total_cost')) || 0), 0);
      doc.text(`Total Amount: $${total.toFixed(2)}`, 20, 50);
      
      let y = 70;
      receiptsForExport.forEach((receipt, i) => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.text(`${formatDate(getEffectiveValue(receipt, 'date'))} - ${getEffectiveValue(receipt, 'merchant_name')} - $${getEffectiveValue(receipt, 'total_cost')}`, 20, y);
        y += 8;
      });
      
      doc.save(`receipt_report_${new Date().toISOString().split('T')[0]}.pdf`);
      showToast('PDF report generated!');
    } catch (error) {
      console.error('PDF error:', error);
      showToast('Failed to generate PDF', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const generatePDFWithImagesExport = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const receiptsForExport = receiptsToExport;
      
      // Fetch items if needed
      let itemsMap = {};
      if (includeLineItems) {
        for (let i = 0; i < receiptsForExport.length; i++) {
          setProgress({ current: i, total: receiptsForExport.length, message: `Fetching items ${i + 1}/${receiptsForExport.length}...` });
          const items = await fetchReceiptItemsWithTags(token, receiptsForExport[i].uuid);
          itemsMap[receiptsForExport[i].uuid] = items;
        }
      }
      
      await generatePDFWithImages(receiptsForExport, {
        layout: imageLayout,
        imageQuality,
        includeLineItems,
        includePageNumbers,
        includeImageAppendix,
        items: itemsMap,
      }, (current, total, message) => {
        setProgress({ current, total, message });
      });
      
      showToast('PDF with images generated!');
    } catch (error) {
      showToast('Failed to generate PDF', 'error');
    } finally {
      setIsLoading(false);
      setProgress({ current: 0, total: 0, message: '' });
    }
  };

  const downloadCSV = (data, filename) => {
    const csvContent = data.map(row => row.map(cell => {
      if (typeof cell !== 'string') return cell;
      const sanitized = cell.replace(/^[=+\-@]/, "'$&");
      return (sanitized.includes(',') || sanitized.includes('"')) ? `"${sanitized.replace(/"/g, '""')}"` : sanitized;
    }).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Paper elevation={2} sx={{ borderRadius: 2, background: 'rgba(22, 28, 36, 0.8)', border: '1px solid rgba(255, 255, 255, 0.05)', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Choose Export Mode</Typography>
            <Grid container spacing={3}>
              {[
                { id: 'bulk', emoji: '📦', title: 'Bulk Export', desc: 'Export all receipts matching your filters (date, tags, categories)', color: '#3b82f6' },
                { id: 'individual', emoji: '✅', title: 'Select Individual', desc: 'Hand-pick specific receipts for expense reports or reimbursements', color: '#22c55e', isNew: true }
              ].map(mode => (
                <Grid item xs={12} md={6} key={mode.id}>
                  <Paper
                    onClick={() => setExportMode(mode.id)}
                    sx={{
                      p: 3, cursor: 'pointer', transition: 'all 0.2s',
                      borderRadius: 2,
                      border: exportMode === mode.id ? `2px solid ${mode.color}` : '2px solid rgba(255,255,255,0.1)',
                      backgroundColor: exportMode === mode.id ? `${mode.color}15` : 'transparent',
                      '&:hover': { borderColor: mode.color, transform: 'translateY(-2px)' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h5">{mode.emoji}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>{mode.title}</Typography>
                      {mode.isNew && <Chip label="NEW" size="small" sx={{ bgcolor: '#22c55e', color: 'white', height: 20, fontSize: '0.65rem' }} />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">{mode.desc}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        );

      case 2:
        return (
          <Paper elevation={2} sx={{ borderRadius: 2, background: 'rgba(22, 28, 36, 0.8)', border: '1px solid rgba(255, 255, 255, 0.05)', overflow: 'hidden' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon fontSize="small" sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight="500">
                {exportMode === 'individual' ? 'Select Receipts' : 'Configure Filters'}
              </Typography>
            </Box>
            
            {exportMode === 'individual' ? (
              <ReceiptSelector
                receipts={receipts}
                selectedReceipts={selectedReceipts}
                onSelectionChange={setSelectedReceipts}
                isLoading={isLoadingReceipts}
              />
            ) : (
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>Export Type</Typography>
                    <FormControl component="fieldset" size="small">
                      <RadioGroup value={exportType} onChange={(e) => setExportType(e.target.value)}>
                        <FormControlLabel value="item" control={<Radio size="small" />} label={<Typography variant="body2"><strong>Line Item Level</strong></Typography>} />
                        <FormControlLabel value="receipt" control={<Radio size="small" />} label={<Typography variant="body2">Receipt Level</Typography>} />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarMonthIcon fontSize="small" /> Date Range
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <DatePicker label="Start" value={startDate} onChange={setStartDate} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                      <DatePicker label="End" value={endDate} onChange={setEndDate} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                    </Box>
                  </Grid>
                  {exportType === 'item' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>🏷️ Tag Filter</Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Tags</InputLabel>
                          <Select multiple value={selectedTags} label="Select Tags" onChange={(e) => setSelectedTags(e.target.value)}
                            renderValue={(selected) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map(tagId => <Chip key={tagId} label={availableTags.find(t => t.tag_id === tagId)?.name || tagId} size="small" />)}
                            </Box>}>
                            {availableTags.map(tag => (
                              <MenuItem key={tag.tag_id} value={tag.tag_id}>
                                <Checkbox checked={selectedTags.includes(tag.tag_id)} />
                                <ListItemText primary={tag.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>📂 Category Filter</Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Categories</InputLabel>
                          <Select multiple value={selectedCategories} label="Select Categories" onChange={(e) => setSelectedCategories(e.target.value)}
                            renderValue={(selected) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map(cat => <Chip key={cat} label={cat} size="small" />)}
                            </Box>}>
                            {availableCategories.map(cat => (
                              <MenuItem key={cat.name} value={cat.name}>
                                <Checkbox checked={selectedCategories.includes(cat.name)} />
                                <ListItemText primary={`${cat.emoji} ${cat.name}`} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                </Grid>
                <Box sx={{ mt: 3, p: 2, borderRadius: 1, bgcolor: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="primary">Receipts matching filters:</Typography>
                  <Chip label={filteredReceipts.length} color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                </Box>
              </Box>
            )}
          </Paper>
        );

      case 3:
        return (
          <Paper elevation={2} sx={{ borderRadius: 2, background: 'rgba(22, 28, 36, 0.8)', border: '1px solid rgba(255, 255, 255, 0.05)', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Export Options</Typography>
            <ExportOptionsPanel
              exportFormat={exportFormat}
              setExportFormat={setExportFormat}
              imageLayout={imageLayout}
              setImageLayout={setImageLayout}
              imageQuality={imageQuality}
              setImageQuality={setImageQuality}
              includeLineItems={includeLineItems}
              setIncludeLineItems={setIncludeLineItems}
              includePageNumbers={includePageNumbers}
              setIncludePageNumbers={setIncludePageNumbers}
              includeImageAppendix={includeImageAppendix}
              setIncludeImageAppendix={setIncludeImageAppendix}
              // Pass export type options for individual mode
              showExportTypeOption={exportMode === 'individual'}
              exportType={exportType}
              setExportType={setExportType}
            />
          </Paper>
        );

      case 4:
        return (
          <Paper elevation={2} sx={{ borderRadius: 2, background: 'rgba(22, 28, 36, 0.8)', border: '1px solid rgba(255, 255, 255, 0.05)', p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>Ready to Export</Typography>
            
            {/* Summary */}
            <Box sx={{ mb: 3, p: 3, borderRadius: 2, bgcolor: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: '#22c55e', fontWeight: 600 }}>📊 Export Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Receipts</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{receiptsToExport.length}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                    ${receiptsToExport.reduce((sum, r) => sum + (parseFloat(getEffectiveValue(r, 'total_cost')) || 0), 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Format</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {exportFormat === 'csv' ? 'CSV' : exportFormat === 'pdf' ? 'PDF' : 'PDF + Images'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Data Level</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{exportType === 'item' ? 'Line Items' : 'Receipts'}</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Data Accuracy Note */}
            <Alert 
              severity="info" 
              sx={{ 
                mb: 4,
                backgroundColor: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                '& .MuiAlert-icon': { color: '#60a5fa' }
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.85rem' }}>
                Data Accuracy
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.5 }}>
                Your export will include the most accurate data available. For fields you've manually edited, 
                your edited values will be used. Otherwise, AI-extracted values will be used (which you should 
                double-check if important). Discount line items may not be included unless specifically selected 
                by category, tag, or other criteria.
              </Typography>
            </Alert>

            {/* Export Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={isLoading || receiptsToExport.length === 0}
                sx={{
                  px: 6, py: 1.5,
                  background: 'linear-gradient(to right, #22c55e, #16a34a)',
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.35)',
                  '&:hover': { background: 'linear-gradient(to right, #16a34a, #15803d)' },
                  fontSize: '1rem', fontWeight: 600, textTransform: 'none'
                }}
              >
                {isLoading ? 'Generating...' : `Export ${receiptsToExport.length} Receipt${receiptsToExport.length !== 1 ? 's' : ''}`}
              </Button>
              
              {isLoading && progress.total > 0 && (
                <Box sx={{ mt: 3, maxWidth: 400, mx: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {progress.message}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(progress.current / progress.total) * 100}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              )}
            </Box>
          </Paper>
        );

      default:
        return null;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: 4, position: 'relative', overflow: 'hidden' }}>
        <BackgroundOrbs />
        
        {/* Header */}
        <Box sx={{ mb: 3, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, display: 'inline-flex', alignItems: 'center', gap: 1,
            background: 'linear-gradient(to right, #60a5fa, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Export Center
            <Chip label="BETA" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold', backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#38bdf8', border: '1px solid rgba(59, 130, 246, 0.3)' }} />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Export your receipt data as CSV files or PDF reports with images
          </Typography>
        </Box>

        {/* Feedback Banner */}
        <Paper elevation={0} sx={{ mb: 3, maxWidth: 800, mx: 'auto', borderRadius: 2, border: '1px solid rgba(121, 80, 242, 0.3)',
          background: 'linear-gradient(135deg, rgba(121, 80, 242, 0.08) 0%, rgba(121, 80, 242, 0.03) 100%)', position: 'relative', zIndex: 1 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(121, 80, 242, 0.15)', borderRadius: '50%', p: 1, height: 40, width: 40 }}>
              <FeedbackIcon fontSize="small" sx={{ color: '#a78bfa' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#7c3aed' }}>Help us improve</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                Share your feedback through our <Button component={Link} href="https://forms.gle/JJoe8sS6mPFvRdgw9" size="small" target="_blank" sx={{ ml: 0.5, fontWeight: 600, color: '#7c3aed' }}>feedback form</Button>
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Use Cases Section */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <UseCasesSection onSelectMode={(mode) => { setExportMode(mode); setCurrentStep(2); }} />
        </Box>

        {/* Step Indicator */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <StepIndicator currentStep={currentStep} onStepClick={handleStepClick} />
        </Box>

        {/* Step Content */}
        <Box sx={{ position: 'relative', zIndex: 1, mb: 3 }}>
          {renderStepContent()}
        </Box>

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={currentStep === 1}
            sx={{ textTransform: 'none' }}
          >
            Back
          </Button>
          {currentStep < 4 && (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={!canGoNext()}
              sx={{ textTransform: 'none', background: 'linear-gradient(to right, #3b82f6, #2563eb)' }}
            >
              Continue
            </Button>
          )}
        </Box>

        {/* Footer */}

        <Snackbar open={toast.open} autoHideDuration={6000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>{toast.message}</Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
}

export default ExportCenter;
